import * as types from "@/redux/ActionTypes";
import { DashboardNotification } from "@/types/RootState";

// Notification type for global notifications
interface Notification {
  id: string | number;
  message: string;
  type?: string;
  [key: string]: unknown;
}

// Action interfaces
interface AddHistoryAction {
  type: typeof types.ADD_HISTORY;
}

interface BackHistoryAction {
  type: typeof types.BACK_HISTORY;
}

interface SetIsMobileAction {
  type: typeof types.SET_IS_MOBILE;
  data: { isMobile: boolean };
}

interface ShowCanvasMenuAction {
  type: typeof types.SHOW_CANVAS_MENU;
  data: boolean;
}

interface LogoPathAction {
  type: typeof types.LOGO_PATH;
  data: string;
}

interface MenuTypeAction {
  type: typeof types.MENU_TYPE;
  data: "MAIN" | "ADMIN";
}

interface ReloadSidebarAction {
  type: typeof types.RELOAD_SIDEBAR;
}

interface DashboardNotificationAction {
  type: typeof types.DASHBOARD_NOTIFICATION;
  data: DashboardNotification | null;
}

interface GlobalNotificationAction {
  type: typeof types.GLOBAL_NOTIFICATION;
  data: Notification[];
}

interface GlobalNotificationCountAction {
  type: typeof types.GLOBAL_NOTIFICATION_COUNT;
  data: number;
}

interface ShowNotificationAction {
  type: typeof types.SHOW_NOTIFICATION;
  data: boolean;
}

interface PreventSidebarAction {
  type: typeof types.PREVENT_SIDEBAR;
  data: boolean;
}

interface HidePlatformHeaderAction {
  type: typeof types.HIDE_PLATFORM_HEADER;
  data: boolean;
}

interface ShowAiChatAction {
  type: typeof types.SHOW_AI_CHAT;
  data: boolean;
}

interface DefaultAction {
  type: string;
  data?: unknown;
}

type AllAction =
  | AddHistoryAction
  | BackHistoryAction
  | SetIsMobileAction
  | ShowCanvasMenuAction
  | LogoPathAction
  | MenuTypeAction
  | ReloadSidebarAction
  | DashboardNotificationAction
  | GlobalNotificationAction
  | GlobalNotificationCountAction
  | ShowNotificationAction
  | PreventSidebarAction
  | HidePlatformHeaderAction
  | ShowAiChatAction
  | DefaultAction;

export interface AllState {
  historyCount: number;
  isMobile: boolean;
  reloadSidebar: boolean;
  notifications: Notification[];
  newNotificationsCount: number;
  preventSidebar: boolean;
  showCanvasMenu?: boolean;
  logoPath?: string;
  menuType?: "MAIN" | "ADMIN";
  dashboardNotification?: DashboardNotification | null;
  showNotification?: boolean;
  showAiChat?: boolean;
  hidePlatformHeader?: boolean;
}

const initialState: AllState = {
  historyCount: 0,
  isMobile: false,
  reloadSidebar: false,
  notifications: [],
  newNotificationsCount: 0,
  preventSidebar: false,
};

const myReducer = (state: AllState = initialState, action: AllAction): AllState => {
  switch (action.type) {
    case types.ADD_HISTORY:
      return { ...state, historyCount: state.historyCount + 1 };
    case types.BACK_HISTORY:
      return { ...state, historyCount: state.historyCount - 1 };
    case types.SET_IS_MOBILE:
      return { ...state, isMobile: (action as SetIsMobileAction).data.isMobile };

    case types.SHOW_CANVAS_MENU:
      return { ...state, showCanvasMenu: (action as ShowCanvasMenuAction).data };
    case types.LOGO_PATH:
      return { ...state, logoPath: (action as LogoPathAction).data };
    case types.MENU_TYPE:
      return { ...state, menuType: (action as MenuTypeAction).data };
    case types.RELOAD_SIDEBAR:
      return { ...state, reloadSidebar: !state.reloadSidebar };
    case types.DASHBOARD_NOTIFICATION:
      return { ...state, dashboardNotification: (action as DashboardNotificationAction).data };
    case types.GLOBAL_NOTIFICATION:
      return {
        ...state,
        notifications: (action as GlobalNotificationAction).data,
      };
    case types.GLOBAL_NOTIFICATION_COUNT:
      return {
        ...state,
        newNotificationsCount: (action as GlobalNotificationCountAction).data,
      };
    case types.SHOW_NOTIFICATION:
      return { ...state, showNotification: (action as ShowNotificationAction).data };
    case types.PREVENT_SIDEBAR:
      return { ...state, preventSidebar: (action as PreventSidebarAction).data };
    case types.HIDE_PLATFORM_HEADER:
      return { ...state, hidePlatformHeader: (action as HidePlatformHeaderAction).data };
    case types.SHOW_AI_CHAT:
      return { ...state, showAiChat: (action as ShowAiChatAction).data };
    default:
      return { ...state };
  }
};

export default myReducer;
